/**
 * Clearing the Firestore disk cache after a sign-out, without killing Firestore
 * in the user's other open tabs.
 *
 * `logout()` (src/context/auth-context.tsx) cannot clear the cache itself:
 * clearing needs the Firestore instance stopped for good, which would leave the
 * rest of that SPA session unable to read anything. So it sets a localStorage
 * flag, and the next app load clears the cache before its own listeners attach
 * (src/firebase/client.ts calls this at module scope).
 *
 * THE CACHE IS ONE INDEXEDDB DATABASE, SHARED BY EVERY OPEN TAB, and deleting it
 * while another tab has it open is not refused. The delete waits for that tab's
 * connection, and Firestore in that tab (4.8.0) answers the deletion by shutting
 * its own client down: it logs "Terminating Firestore due to IndexedDb database
 * deletion" and nothing throws after that. Every later read, listener and write
 * in that tab waits forever, so its page stops updating and the next page it
 * opens never loads (a product page sits on its server-rendered body). Only a
 * reload recovers it. The trigger was ordinary: sign out, buy as a guest (a card
 * checkout never reloads the page), then open the emailed access link, which
 * loads a new tab while the product tab is still open.
 *
 * So the wipe only runs in a tab that is alone. Every tab holds OPEN_TAB_LOCK in
 * shared mode for as long as it is open, and a flagged tab asks for the same
 * lock exclusively without waiting (`ifAvailable`). Getting it proves no other
 * tab is open; not getting it leaves the flag for a later load. The browser
 * releases a tab's locks when the tab closes or crashes, so there is no
 * heartbeat to go stale.
 *
 * Deferring only costs time: the previous session's documents stay on disk
 * until a load that finds no other tab open, the residual exposure client.ts
 * already accepts, for longer. A deferred wipe never breaks a tab. Browsers
 * without Web Locks (Safari before 15.4, Firefox before 96) cannot prove they
 * are alone, so they keep the flag and never wipe.
 */

/** Held in shared mode by every open tab, for the life of the tab. */
export const OPEN_TAB_LOCK = 'supa-place:open-tab';

/** The slice of `navigator.locks` used here, so tests can hand in a fake. */
export interface TabLockManager {
  request(
    name: string,
    options: { mode: 'shared' | 'exclusive'; ifAvailable?: boolean },
    callback: (lock: unknown) => unknown,
  ): Promise<unknown>;
}

/** The slice of `localStorage` used here. */
export interface FlagStorage {
  getItem(key: string): string | null;
  removeItem(key: string): void;
}

export type CacheWipeOutcome =
  /** No flag set, or no readable storage that could hold one. */
  | 'not-flagged'
  /** Cache deleted and the flag cleared. */
  | 'wiped'
  /** Another tab is open. The flag stays for a later load. */
  | 'deferred'
  /** Firestore refused (it was already in use) or the delete failed. The flag stays. */
  | 'failed'
  /** No Web Locks, so no way to know this tab is alone. The flag stays. */
  | 'unsupported';

/**
 * Register this tab as open and, if a sign-out left the flag, clear the cache
 * when no other tab is open. Call once per page load, before any Firestore
 * listener attaches.
 *
 * `clearCache` must throw or reject when it cannot clear. Firestore's
 * `clearIndexedDbPersistence` does both: it throws synchronously once the
 * instance is in use, which can now happen because the lock answer arrives
 * asynchronously. Either way the flag stays and a later load tries again.
 */
export function wipeCacheIfFlagged(options: {
  flagKey: string;
  storage: FlagStorage | null;
  locks: TabLockManager | null | undefined;
  clearCache: () => Promise<unknown>;
}): Promise<CacheWipeOutcome> {
  const { flagKey, storage, locks, clearCache } = options;

  let flagged = false;
  try {
    flagged = storage?.getItem(flagKey) === '1';
  } catch {
    // Storage is blocked, so no flag can have been written to it either.
  }

  if (!locks) return Promise.resolve(flagged ? 'unsupported' : 'not-flagged');

  let outcome: Promise<CacheWipeOutcome> = Promise.resolve('not-flagged');
  if (flagged) {
    outcome = requestLock(locks, { mode: 'exclusive', ifAvailable: true }, async (lock) => {
      if (!lock) return 'deferred';
      try {
        await clearCache();
      } catch {
        return 'failed';
      }
      try {
        storage?.removeItem(flagKey);
      } catch {
        // Cleared regardless. A flag left behind only makes a later load
        // clear an empty cache.
      }
      return 'wiped';
    }).then((result) => result as CacheWipeOutcome, () => 'failed' as const);
  }

  // Asked for in the same turn as the exclusive request above, so it queues
  // behind that request instead of racing it. There is never a moment when this
  // tab holds neither lock, which is when another tab could decide it was alone.
  // The callback never settles, so the lock is held until the tab goes away.
  void requestLock(locks, { mode: 'shared' }, () => new Promise<never>(() => undefined)).catch(() => undefined);

  return outcome;
}

/** `locks.request`, with a synchronous throw turned into a rejection. */
function requestLock(
  locks: TabLockManager,
  options: { mode: 'shared' | 'exclusive'; ifAvailable?: boolean },
  callback: (lock: unknown) => unknown,
): Promise<unknown> {
  try {
    return locks.request(OPEN_TAB_LOCK, options, callback);
  } catch (error) {
    return Promise.reject(error);
  }
}
